/**
 * Validate IBAN format: 2 letter country code + 2 check digits + 11-30 alphanumeric chars.
 * Returns null if valid, error message if invalid.
 * Exported for direct unit testing — see EmployeeSettings.validateIban.test.ts.
 * Kept in sync by hand with the backend copy in
 * supabase/functions/_shared/services/employeeService.ts (see that file's own
 * comment) — there is no shared validation package between the two runtimes.
 *
 * Deliberately kept in its own module (no other imports) rather than defined
 * inline in EmployeeSettings.tsx: that file imports ServiceRegistry, which
 * eagerly pulls in the Supabase client singleton at module scope, so a test
 * importing validateIban from EmployeeSettings.tsx would crash outside a
 * browser-like (jsdom) test environment purely from that unrelated import
 * chain — not from anything validateIban itself needs.
 */
export type IbanError = "length" | "format";

/** Which rule an IBAN breaks, if any; the screen words it from the catalog, `validateIban` in English. */
export function ibanError(iban: string): IbanError | null {
  const cleaned = iban.replace(/\s/g, "").toUpperCase();
  if (cleaned.length < 15 || cleaned.length > 34) return "length";
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(cleaned)) return "format";
  return null;
}

const IBAN_MESSAGES: Record<IbanError, string> = {
  length: "IBAN must be between 15 and 34 characters",
  format: "IBAN must start with a country code (e.g. SE) followed by digits and alphanumeric characters",
};

export function validateIban(iban: string): string | null {
  const error = ibanError(iban);
  return error ? IBAN_MESSAGES[error] : null;
}
